import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { BrowseTypesEnum } from '../models/browse-types.enum';
import { CoachingStore } from '../store/coaching/coaching.store';
import { GpsStore } from '../store/gps/gps.store';
import { MapStore } from '../store/map/map.store';
import { SearchedContentStore } from '../store/searched-content/searched-content.store';
import { VideoStore } from '../store/video/video.store';
import { WorkoutStore } from '../store/workout/workout.store';
import { Playlist } from '@kinomap-dev/ng-ui-lib/lib/models/playlist.model';
import { PlaylistStore } from '../store/playlist/playlist.store';
import {
  GpsOnly,
  VideoSection,
  Workout,
  Coaching,
} from '@kinomap-dev/ng-ui-lib';

@Injectable({
  providedIn: 'root',
})
export class FavoriteService {
  constructor(
    private videoStore: VideoStore,
    private searchedContentStore: SearchedContentStore,
    private mapStore: MapStore,
    private gpsStore: GpsStore,
    private workoutStore: WorkoutStore,
    private coachingStore: CoachingStore,
    private playlistStore: PlaylistStore
  ) { }

  addOrRemoveFavorite(
    contentType: BrowseTypesEnum | string,
    id: number,
    isAdding: boolean = true
  ): void {
    switch (contentType) {
      case BrowseTypesEnum.Video:
      case 'watch':
        this.addOrRemoveVideo(id, isAdding);
        return;
      case BrowseTypesEnum.GpsTrack:
        this.addOrRemoveGpsTrack(id, isAdding);
        return;
      case BrowseTypesEnum.Workout:
        this.addOrRemoveWorkout(id, isAdding);
        return;
      case BrowseTypesEnum.Coaching:
        this.addOrRemoveCoaching(id, isAdding);
        return;
      case BrowseTypesEnum.Playlist:
        this.addOrRemovePlaylist(id, isAdding);
        return;
      default:
        return;
    }
  }

  private async addOrRemoveVideo(id: number, isAdding: boolean): Promise<void> {
    {
      const obj: any = {
        videos: {
          content: await firstValueFrom(this.videoStore.videos$),
          method: this.videoStore.setVideos.bind(this.videoStore),
        },
        homeVideos: {
          content: await firstValueFrom(this.videoStore.homeVideos$),
          method: this.videoStore.setHomeVideos.bind(this.videoStore),
        },
        videosOfTheWeek: {
          content: await firstValueFrom(this.videoStore.videosOfTheWeek$),
          method: this.videoStore.setVideosOfTheWeek.bind(this.videoStore),
        },
        recommendedVideos: {
          content: await firstValueFrom(this.videoStore.recommendedVideos$),
          method: this.videoStore.setRecommendedVideos.bind(this.videoStore),
        },
        top10Videos: {
          content: await firstValueFrom(this.videoStore.top10Videos$),
          method: this.videoStore.setTop10Videos.bind(this.videoStore),
        },
        mapVideos: {
          content: await firstValueFrom(this.mapStore.videos$),
          method: this.mapStore.setVideos.bind(this.mapStore),
        },
      };

      const searchedContents: VideoSection[] | Coaching[] =
        await firstValueFrom(this.searchedContentStore.searchedContents$);
      if (
        searchedContents.length &&
        searchedContents[0].type === BrowseTypesEnum.Video
      ) {
        obj.searchedContents = {
          content: searchedContents,
          method: this.searchedContentStore.setSearchedContents.bind(
            this.searchedContentStore
          ),
        };
      }

      for (const key of Object.keys(obj)) {
        const { content, method } = obj[key];
        method(
          content.map((video: VideoSection) => {
            if (video.id === id) {
              return { ...video, isFavorite: isAdding };
            }
            return video;
          })
        );
      }
    }
  }

  private async addOrRemoveGpsTrack(
    id: number,
    isAdding: boolean
  ): Promise<void> {
    const gpsTracksToUpdate: GpsOnly[] = await firstValueFrom(
      this.gpsStore.gpsTracks$
    );
    this.gpsStore.setGpsTracks(
      gpsTracksToUpdate.map((gpsTrack: GpsOnly) => {
        if (gpsTrack.id === id) {
          return { ...gpsTrack, isFavorite: isAdding };
        }
        return gpsTrack;
      })
    );
  }

  private async addOrRemoveWorkout(
    id: number,
    isAdding: boolean
  ): Promise<void> {
    const workoutsToUpdate: Workout[] = await firstValueFrom(
      this.workoutStore.workouts$
    );
    this.workoutStore.setWorkouts(
      workoutsToUpdate.map((workout: Workout) => {
        if (workout.id === id) {
          return { ...workout, isFavorite: isAdding };
        }
        return workout;
      })
    );
  }

  private async addOrRemoveCoaching(
    id: number,
    isAdding: boolean
  ): Promise<void> {
    const coachingsToUpdate: Coaching[] = await firstValueFrom(
      this.coachingStore.coachings$
    );
    this.coachingStore.setCoachings(
      coachingsToUpdate.map((coaching: Coaching) => {
        if (coaching.id === id) {
          return { ...coaching, isFavorite: isAdding };
        }
        return coaching;
      })
    );

    const searchedContentsToUpdate: VideoSection[] | Coaching[] =
      await firstValueFrom(this.searchedContentStore.searchedContents$);
    if (
      !searchedContentsToUpdate.length ||
      searchedContentsToUpdate[0].type !== BrowseTypesEnum.Coaching
    ) {
      return;
    }
    this.searchedContentStore.setSearchedContents(
      (searchedContentsToUpdate as Coaching[]).map((coaching: Coaching) => {
        if (coaching.id === id) {
          return { ...coaching, isFavorite: isAdding };
        }
        return coaching;
      })
    );
  }

  private async addOrRemovePlaylist(
    id: number,
    isAdding: boolean
  ): Promise<void> {
    const playlistsToUpdate: Playlist[] = await firstValueFrom(
      this.playlistStore.playlists$
    );
    this.playlistStore.setPlaylists(
      playlistsToUpdate.map((playlist: Playlist) => {
        if (playlist.id === id) {
          return { ...playlist, isFavorite: isAdding };
        }
        return playlist;
      })
    );

    const homePlaylistsToUpdate: Playlist[] = await firstValueFrom(
      this.playlistStore.homePlaylists$
    );
    this.playlistStore.setHomePlaylists(
      homePlaylistsToUpdate.map((playlist: Playlist) => {
        if (playlist.id === id) {
          return { ...playlist, isFavorite: isAdding };
        }
        return playlist;
      })
    );

    const searchedPlaylistsToUpdate: Playlist[] = await firstValueFrom(
      this.searchedContentStore.searchedPlaylists$
    );

    this.searchedContentStore.setSearchedPlaylists(
      searchedPlaylistsToUpdate.map((playlist: Playlist) => {
        if (playlist.id === id) {
          return { ...playlist, isFavorite: isAdding };
        }
        return playlist;
      })
    );
  }
}
